import React, { memo, HTMLProps } from "react";
import classnames from "classnames";
import { twMerge } from "tailwind-merge";
import { Element, SIZE, STATE } from "../base";

interface IProps extends HTMLProps<HTMLButtonElement | HTMLLinkElement> {
    className?: string;
    children: React.ReactNode;
    renderAs?: string;
    outlined?: boolean;
    inverted?: boolean;
    uppered?: boolean;
    isSize?: SIZE;
    state?: STATE;
    submit?: boolean;
    reset?: boolean;
    fullwidth?: boolean;
    loading?: boolean;
    disabled?: boolean;
    remove?: boolean;
    isSelected?: boolean;
    isStatic?: boolean;
    onClick?: (evt: any) => any;
    domRef?: any;
}

function Button(props: IProps) {
    const {
        children,
        className = "",
        renderAs = "button",
        color,
        size,
        isSize,
        outlined,
        inverted,
        uppered,
        state,
        submit,
        reset,
        fullwidth,
        loading,
        disabled,
        remove,
        isSelected,
        isStatic,
        onClick,
        domRef,
        ...rest
    } = props;
    let otherProps = {};
    if (submit) {
        otherProps = {
            type: "submit",
            renderAs: renderAs || "button",
        };
    }
    if (reset) {
        otherProps = {
            type: "reset",
            renderAs: renderAs || "button",
        };
    }

    if (isStatic) {
        otherProps = {
            renderAs: "span",
        };
    }

    return (
        <Element
            tabIndex={disabled ? -1 : 0}
            renderAs={renderAs}
            domRef={domRef}
            {...rest}
            {...otherProps}
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            className={twMerge(
                "relative",
                "text-[1.4rem]",
                "leading-[1.67]",
                "inline-block",
                "font-normal",
                "text-center",
                "cursor-pointer",
                "normal-case",
                "border-0",
                "transition-button",
                "disabled:shadow-none disabled:opacity-50 disabled:cursor-not-allowed",
                classnames(
                    {
                        //colors
                        ["bg-[color:var(--primary)] text-[color:var(--white)] border-[color:var(--primary)]"]:
                            color === "primary",
                        ["bg-[color:var(--red)] text-[color:var(--white)] border-[color:var(--red)] hover:bg-[#d23430]"]:
                            color === "danger",
                        ["bg-[color:var(--yellow)] !text-[color:var(--black)]"]:
                            color === "yellow",
                        ["bg-[color:var(--gray)] !text-[color:var(--black)]"]:
                            color === "gray",

                        //size
                        ["text-[1.4rem] px-[24px] py-[9px]"]: isSize === "medium",
                        ["text-[1.2rem] px-[19px] py-[6px]"]: isSize === "small",

                        // state
                        [classnames(
                            "relative",
                            "pointer-events-none",
                            "!text-transparent",
                            "after:absolute after:w-[1em] after:h-[1em] after:left-1/2 after:top-1/2",
                            "after:mt-[-0.5em] after:ml-[-0.5em]",
                            "after:animate-spin",
                            "after:rounded-[999px]",
                            "after:border-[#dbdbdb] after:border-r-transparent after:border-t-transparent after:border-2 after:border-solid "
                        )]: loading,
                        //outlined
                        [classnames(
                            "flex justify-center items-center",
                            "bg-[var(--alpha-bg)]",
                            "border border-solid border-[var(--border-primary)]",
                            "text-[var(--text-primary) my-0 mx-auto]"
                        )]: outlined,
                        ["is-selected"]: isSelected,
                        ["is-static"]: isStatic,
                        ["is-inverted"]: inverted,
                        ["px-0 py-[8px]"]: fullwidth,
                        ["is-upper"]: uppered,
                        ["delete"]: remove,
                    },
                    className
                )
            )}
        >
            {children}
        </Element>
    );
}

export default memo(Button);
